import React, { useState } from 'react';

interface RulesPanelProps {
  rules: {
    bannedWords: string[];
    requiredWords: string[];
    wordLengthPattern: number[] | null;
    shouldRhyme: boolean;
  };
  onRuleChange: (rules: RulesPanelProps['rules']) => void;
  isWriting: boolean;
}

const RulesPanel: React.FC<RulesPanelProps> = ({ rules, onRuleChange, isWriting }) => {
  const [newBannedWord, setNewBannedWord] = useState('');
  const [newRequiredWord, setNewRequiredWord] = useState('');
  const [newPatternValue, setNewPatternValue] = useState('');
  const [activeTab, setActiveTab] = useState<'banned' | 'required' | 'pattern' | 'rhyme'>('banned');

  const addBannedWord = () => {
    if (newBannedWord.trim() && !rules.bannedWords.includes(newBannedWord.trim())) {
      const updatedRules = {
        ...rules,
        bannedWords: [...rules.bannedWords, newBannedWord.trim()]
      };
      onRuleChange(updatedRules);
      setNewBannedWord('');
    }
  };

  const removeBannedWord = (word: string) => {
    const updatedRules = {
      ...rules,
      bannedWords: rules.bannedWords.filter(w => w !== word)
    };
    onRuleChange(updatedRules);
  };

  const addRequiredWord = () => {
    if (newRequiredWord.trim() && !rules.requiredWords.includes(newRequiredWord.trim())) {
      const updatedRules = {
        ...rules,
        requiredWords: [...rules.requiredWords, newRequiredWord.trim()]
      };
      onRuleChange(updatedRules);
      setNewRequiredWord('');
    }
  };

  const removeRequiredWord = (word: string) => {
    const updatedRules = {
      ...rules,
      requiredWords: rules.requiredWords.filter(w => w !== word)
    };
    onRuleChange(updatedRules);
  };

  const addToWordLengthPattern = () => {
    const numValue = parseInt(newPatternValue);
    if (!isNaN(numValue) && numValue > 0) {
      const currentPattern = rules.wordLengthPattern || [];
      const updatedRules = {
        ...rules,
        wordLengthPattern: [...currentPattern, numValue]
      };
      onRuleChange(updatedRules);
      setNewPatternValue('');
    }
  };

  const clearWordLengthPattern = () => {
    const updatedRules = {
      ...rules,
      wordLengthPattern: null
    };
    onRuleChange(updatedRules);
  };

  const toggleRhymeRule = () => {
    const updatedRules = {
      ...rules,
      shouldRhyme: !rules.shouldRhyme
    };
    onRuleChange(updatedRules);
  };

  return (
    <div className="rules-panel mb-6 bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex border-b border-gray-200">
        <button 
          onClick={() => setActiveTab('banned')} 
          className={`flex-1 py-2 px-4 text-sm font-medium ${activeTab === 'banned' ? 'bg-indigo-50 text-indigo-700 border-b-2 border-indigo-500' : 'text-gray-600 hover:text-indigo-600 hover:bg-gray-100'}`}
          disabled={isWriting}
        >
          Banned Words
          {rules.bannedWords.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-xs">
              {rules.bannedWords.length}
            </span>
          )}
        </button>
        <button 
          onClick={() => setActiveTab('required')} 
          className={`flex-1 py-2 px-4 text-sm font-medium ${activeTab === 'required' ? 'bg-indigo-50 text-indigo-700 border-b-2 border-indigo-500' : 'text-gray-600 hover:text-indigo-600 hover:bg-gray-100'}`}
          disabled={isWriting}
        >
          Required Words
          {rules.requiredWords.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs">
              {rules.requiredWords.length}
            </span>
          )}
        </button>
        <button 
          onClick={() => setActiveTab('pattern')} 
          className={`flex-1 py-2 px-4 text-sm font-medium ${activeTab === 'pattern' ? 'bg-indigo-50 text-indigo-700 border-b-2 border-indigo-500' : 'text-gray-600 hover:text-indigo-600 hover:bg-gray-100'}`}
          disabled={isWriting}
        >
          Word Pattern
          {rules.wordLengthPattern && rules.wordLengthPattern.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">
              {rules.wordLengthPattern.length}
            </span>
          )}
        </button>
        <button 
          onClick={() => setActiveTab('rhyme')} 
          className={`flex-1 py-2 px-4 text-sm font-medium ${activeTab === 'rhyme' ? 'bg-indigo-50 text-indigo-700 border-b-2 border-indigo-500' : 'text-gray-600 hover:text-indigo-600 hover:bg-gray-100'}`}
          disabled={isWriting}
        >
          Rhyming
          {rules.shouldRhyme && (
            <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full text-xs">
              On
            </span>
          )}
        </button>
      </div>
      
      <div className="p-4">
        {/* Banned Words Tab */}
        {activeTab === 'banned' && (
          <div className="space-y-3">
            <div className="text-sm text-gray-600">Words that cannot appear in your writing</div>
            <div className="flex">
              <input
                type="text"
                value={newBannedWord}
                onChange={(e) => setNewBannedWord(e.target.value)}
                placeholder="Add word to ban"
                disabled={isWriting}
                className="flex-1 p-2 border border-gray-300 rounded-l-md focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
              />
              <button
                onClick={addBannedWord}
                disabled={isWriting || !newBannedWord.trim()}
                className="px-3 py-2 bg-indigo-600 text-white rounded-r-md hover:bg-indigo-700 focus:outline-none disabled:opacity-50"
              >
                Add
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {rules.bannedWords.length > 0 ? (
                rules.bannedWords.map((word, index) => (
                  <div key={index} className="bg-red-100 text-red-800 text-sm py-1 px-2 rounded-md flex items-center">
                    {word}
                    {!isWriting && (
                      <button 
                        onClick={() => removeBannedWord(word)} 
                        className="ml-1 text-red-500 hover:text-red-700 focus:outline-none"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-500 italic">No banned words set</div>
              )}
            </div>
          </div>
        )}

        {/* Required Words Tab */}
        {activeTab === 'required' && (
          <div className="space-y-3">
            <div className="text-sm text-gray-600">Words that must appear in your writing</div>
            <div className="flex">
              <input
                type="text"
                value={newRequiredWord}
                onChange={(e) => setNewRequiredWord(e.target.value)}
                placeholder="Add required word"
                disabled={isWriting}
                className="flex-1 p-2 border border-gray-300 rounded-l-md focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
              />
              <button
                onClick={addRequiredWord}
                disabled={isWriting || !newRequiredWord.trim()}
                className="px-3 py-2 bg-indigo-600 text-white rounded-r-md hover:bg-indigo-700 focus:outline-none disabled:opacity-50"
              >
                Add
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {rules.requiredWords.length > 0 ? (
                rules.requiredWords.map((word, index) => (
                  <div key={index} className="bg-green-100 text-green-800 text-sm py-1 px-2 rounded-md flex items-center">
                    {word}
                    {!isWriting && (
                      <button 
                        onClick={() => removeRequiredWord(word)} 
                        className="ml-1 text-green-500 hover:text-green-700 focus:outline-none"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-500 italic">No required words set</div>
              )}
            </div>
          </div>
        )}

        {/* Word Length Pattern Tab */}
        {activeTab === 'pattern' && (
          <div className="space-y-3">
            <div className="text-sm text-gray-600">
              Words must follow this sequence of lengths (e.g., 3-5-2 means words of 3, then 5, then 2 letters, repeating)
            </div>
            <div className="flex">
              <input
                type="number"
                min="1"
                value={newPatternValue}
                onChange={(e) => setNewPatternValue(e.target.value)}
                placeholder="Word length"
                disabled={isWriting}
                className="flex-1 p-2 border border-gray-300 rounded-l-md focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
              />
              <button
                onClick={addToWordLengthPattern}
                disabled={isWriting || !newPatternValue}
                className="px-3 py-2 bg-indigo-600 text-white rounded-r-md hover:bg-indigo-700 focus:outline-none disabled:opacity-50"
              >
                Add
              </button>
            </div>
            {rules.wordLengthPattern && rules.wordLengthPattern.length > 0 ? (
              <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-center justify-between">
                  <span className="text-blue-800">Pattern: {rules.wordLengthPattern.join('-')}</span>
                  {!isWriting && (
                    <button 
                      onClick={clearWordLengthPattern}
                      className="text-blue-500 hover:text-blue-700 focus:outline-none text-sm"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500 italic mt-3">No pattern set</div>
            )}
          </div>
        )}

        {/* Rhyme Tab */}
        {activeTab === 'rhyme' && (
          <div className="space-y-3">
            <div className="text-sm text-gray-600">
              When activated, adjacent sentences should end with rhyming words
            </div>
            <div className="flex items-center space-x-2 p-2 bg-purple-50 border border-purple-200 rounded-md">
              <input
                type="checkbox"
                id="rhymeToggle"
                checked={rules.shouldRhyme}
                onChange={toggleRhymeRule}
                disabled={isWriting}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="rhymeToggle" className="text-sm text-gray-700">
                {rules.shouldRhyme ? 'Rhyming is enabled' : 'Enable rhyming'}
              </label>
            </div>
          </div>
        )}
      </div>
      
      {isWriting && (
        <div className="p-3 bg-yellow-50 border-t border-yellow-200 text-center">
          <p className="text-sm text-yellow-700">
            Rules are locked during writing. Finish your session to edit rules.
          </p>
        </div>
      )}
    </div>
  );
};

export default RulesPanel; 